import React, { Component, lazy, Suspense } from "react";
import type { MultilingualAudioPlayerProps } from "./multilingual-audio-player";

/** Nouvelle tentative si le chunk a échoué (réseau / onglet longtemps en arrière-plan). */
const MultilingualAudioPlayer = lazy(async () => {
  try {
    return await import("./multilingual-audio-player");
  } catch (err) {
    console.warn("Lecteur audio : échec du chargement du module, nouvelle tentative…", err);
    await new Promise((r) => setTimeout(r, 400));
    return import("./multilingual-audio-player");
  }
});

/** Placeholder pour éviter le layout shift (hauteur approximative du lecteur) */
const Fallback = () => (
  <div
    className="rounded-lg bg-gray-100 animate-pulse min-h-[120px] flex items-center justify-center"
    aria-hidden
  >
    <span className="text-gray-400 text-sm">Chargement du lecteur…</span>
  </div>
);

type BoundaryState = { hasError: boolean; resetKey: number };

/**
 * Évite de faire tomber toute la route (errorElement React Router) si le lazy
 * ou le lecteur lève une erreur après veille / chunk manquant.
 */
class AudioPlayerErrorBoundary extends Component<
  { children: React.ReactNode },
  BoundaryState
> {
  state: BoundaryState = { hasError: false, resetKey: 0 };

  static getDerivedStateFromError(): Partial<BoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.warn("AudioPlayerErrorBoundary:", error);
  }

  handleRetry = () => {
    this.setState((s) => ({ hasError: false, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-800 shadow-sm">
          <p className="mb-3">
            Le lecteur audio n’a pas pu se charger (connexion ou onglet inactif).
            Vous pouvez réessayer sans recharger toute la page.
          </p>
          <button
            type="button"
            className="rounded-full bg-gray-900 px-4 py-2 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500"
            onClick={this.handleRetry}
          >
            Réessayer le lecteur
          </button>
        </div>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

/**
 * Version chargée à la demande du lecteur audio multilingue.
 * Réduit le bundle initial des pages produit.
 */
const LazyMultilingualAudioPlayer: React.FC<MultilingualAudioPlayerProps> = (
  props
) => (
  <AudioPlayerErrorBoundary>
    <Suspense fallback={<Fallback />}>
      <MultilingualAudioPlayer {...props} />
    </Suspense>
  </AudioPlayerErrorBoundary>
);

export default LazyMultilingualAudioPlayer;
